/**
 * Submission hooks (PCO → submission → weekly rollup).
 *
 *   usePcoSubmissions(pcoId)   grouped sections on the rollups tab
 *
 * Distinct from useRollups.ts's `useSubmissions`, which backs the send-audit
 * history panel (rollup_sends). Refetch after close-and-next / open-alongside /
 * patch mutations via the returned refetch().
 */
import { useCallback, useEffect } from 'react';
import { api, ApiCallError } from './api';
import { useApiQuery } from './useApiQuery';
import type { SubmissionErrorCode, SubmissionSummary } from '@coffman-tm/shared';

/**
 * Cross-instance invalidation for the per-PCO submissions query.
 *
 * `useApiQuery` deliberately has no shared cache (single user, human mutation
 * cadence), so every component watching one PCO holds its own copy. That's
 * fine until a mutation in ONE of them changes the set for all: saving an
 * inbox block with "start next submission" opens a package and moves the
 * default, and every sibling block card kept advertising the old one. This is
 * the smallest thing that fixes it — a per-PCO signal that all mounted
 * `usePcoSubmissions` refetch on. Not a cache; just a nudge.
 */
const submissionListeners = new Map<string, Set<() => void>>();

/**
 * Unwrap a submission mutation's response. Every other submission route
 * answers `{ submission }`, but start-next is specified as returning the
 * summary; this accepts either so the two agents' halves can land in any
 * order, and returns null if it's neither.
 */
export function submissionFromResponse(res: unknown): SubmissionSummary | null {
  if (typeof res !== 'object' || res === null) return null;
  const r = res as Record<string, unknown>;
  const wrapped = r.submission;
  const candidate = typeof wrapped === 'object' && wrapped !== null ? wrapped : r;
  const c = candidate as Record<string, unknown>;
  return typeof c.id === 'string' && typeof c.submission_number === 'string'
    ? (candidate as SubmissionSummary)
    : null;
}

/** Refetch every mounted `usePcoSubmissions(pcoId)` — call after a mutation
 *  that changes which submissions are open or which one is the default. */
export function invalidatePcoSubmissions(pcoId: string): void {
  for (const listener of submissionListeners.get(pcoId) ?? []) listener();
}

export function usePcoSubmissions(pcoId: string | null) {
  const fetcher = useCallback(
    (t: string, signal: AbortSignal) => {
      if (!pcoId) return Promise.reject(new Error('usePcoSubmissions: pcoId required'));
      return api.submissions.list(t, pcoId, { signal });
    },
    [pcoId],
  );
  const query = useApiQuery(fetcher, [pcoId]);
  const { refetch } = query;
  useEffect(() => {
    if (!pcoId) return;
    const listeners = submissionListeners.get(pcoId) ?? new Set<() => void>();
    submissionListeners.set(pcoId, listeners);
    const listener = () => void refetch();
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
      if (listeners.size === 0) submissionListeners.delete(pcoId);
    };
  }, [pcoId, refetch]);
  return query;
}

/** Project-wide submissions grouped by PCO — the PCO Log's expand prefetch. */
export function useProjectSubmissions() {
  return useApiQuery((t, signal) => api.submissions.listAll(t, { signal }));
}

/**
 * Human-readable copy for each SubmissionErrorCode the lifecycle/move surfaces
 * can return (`{ error: code }`). Keyed exhaustively off the union so a new
 * code is a compile error until it's given a message. Values are functions of
 * the project's GC display name (Coffman works multiple GCs, so copy can't
 * hardcode "Hoffman") — most ignore the argument.
 */
const SUBMISSION_ERROR_MESSAGES: Record<SubmissionErrorCode, (gc: string) => string> = {
  submission_not_found: () => 'That submission no longer exists. Refresh and try again.',
  submission_not_open: () => 'That submission is closed — reopen it before assigning work.',
  no_open_default: () => 'This PCO has no open submission to close. Refresh and try again.',
  number_conflict: () => 'That submission number is already used on this PCO. Pick another.',
  number_locked: () => 'The number can only be changed while the submission is open.',
  illegal_status_transition: () => "That status change isn't allowed from here.",
  source_week_locked: (gc) => `A week you touched was already sent to ${gc}. Reopen it first.`,
  carry_week_conflict: () =>
    'That submission already bills one of the unsent weeks being carried over. Pick a different number or move the week first.',
  rollup_locked: (gc) => `That week was already sent to ${gc}. Reopen it before moving it.`,
  rollup_not_found: () => 'That week no longer exists. Refresh and try again.',
  week_conflict: () => 'That submission already has a rollup for this week.',
  invalid_destination_submission: () => 'That destination submission is invalid.',
  submission_required_for_split_week: () =>
    'Pick which submission to move into — this week is split.',
  not_ledger_submission: () =>
    'Only a historical (pre-portal) submission can be edited or removed this way — this one has weeks behind it.',
  // The empty-package delete. Both refusals name what changed, because the
  // only way to meet either is a race: the action is hidden on a package that
  // doesn't qualify, so the PM sees these when the package took work (or was
  // closed) between the page load and the click.
  not_deletable_submission: () =>
    'That package can no longer be removed — it is either closed or the one collecting new work. Refresh to see where it stands.',
  submission_has_weeks: () =>
    'That package has weeks in it now, so it can’t be removed. Refresh to see them.',
  ledger_status_locked: () =>
    "A historical submission can't be reopened — its amount would drop out of the totals. Edit the amount, or remove the row and re-enter it.",
  // Body-validation codes. These reach the user through the Add historical
  // submission form, so each one has to name the field and the fix.
  body_required: () => 'Something went wrong with that form. Refresh and try again.',
  number_required: () => 'Enter a submission number.',
  invalid_amount: () => 'Enter an amount greater than zero.',
  invalid_status: () => 'Choose whether this was sent, or already approved to bill.',
  invalid_range_label: () => 'That date range is too long — keep it under 120 characters.',
  invalid_reference: () => 'The MOD and FWA references have to be text.',
  pco_not_found: () => 'That PCO no longer exists. Refresh and try again.',
  not_open: () => 'That submission is no longer open. Refresh and try again.',
  unsent_weeks_exist: () =>
    'This submission still has unsent weeks — send them or use Transfer Week first.',
};

/**
 * Map a thrown API error to display copy, switching on SubmissionErrorCode when
 * the 409/400 payload carries one; otherwise return the caller's fallback.
 * `gc` is the project's GC display name, interpolated into the codes that
 * reference the GC (defaults to the generic "the GC").
 */
export function submissionErrorMessage(err: unknown, fallback: string, gc: string = 'the GC'): string {
  if (err instanceof ApiCallError) {
    return submissionErrorMessageForCode(err.payload.error, fallback, gc);
  }
  return fallback;
}

/**
 * Same mapping for a bare code rather than a thrown error — some endpoints
 * report a partial failure in their 200 body (Approve & Send returns
 * `submission_close_error` when the send succeeded but the close was refused)
 * instead of throwing.
 */
export function submissionErrorMessageForCode(
  code: string | null | undefined,
  fallback: string,
  gc: string = 'the GC',
): string {
  return code && code in SUBMISSION_ERROR_MESSAGES
    ? SUBMISSION_ERROR_MESSAGES[code as SubmissionErrorCode](gc)
    : fallback;
}
